import Dependencies from '../utilities/dependencies';
import CmsApi from '../utilities/cms-api';

export default class PlacesMap {

	/**
	 * CSS selectors that must exist for this module to run
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getHolderSelectors = () => ['.map-element'];

	/**
	 * Dependencies that this module requires
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getDependencies = () => [
		[Dependencies.libraries.googleMaps],
		Dependencies.libraries.snazzyInfoWindow,
	];

	/**
	 * Runtime injected CSS classes
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getClassList = () => Object.create({});

	/** @type {HTMLElement} */
	#holderElement;

	/** @type {google.maps.LatLngBounds} */
	#bounds;

	/** @type {google.maps.Map} */
	#googleMap;

	/** @type {Array<any>} */
	#markers = [];

	/** @type {Boolean} */
	#clearMarkers;

	/** @type {HTMLElement} */
	#toggleButtons;

	/**
	 * @param {HTMLElement} holderElement
	 */
	constructor(holderElement) {
		this.#holderElement = holderElement;
		this.#toggleButtons = this.#holderElement.querySelectorAll('.js-place-map-toggle-btn');
		this.#clearMarkers = true;
		this.onload();
	}

	onload() {
		this.#setGoogleMap();
		this.#initMarkers();
		this.#fetchPlaces();
		this.#setEventListenersOnToggleButtons();
		this.#setThingsToDoMap();
	}

	#setGoogleMap() {
		this.#bounds = new google.maps.LatLngBounds();
		this.#googleMap = new google.maps.Map(this.#holderElement.querySelector('#places-map'),{
			styles: [{"featureType": "all", "elementType": "labels.text", "stylers": [{"color": "#878787"}]}, {"featureType": "all", "elementType": "labels.text.stroke", "stylers": [{"visibility": "off"}]},{"featureType": "landscape", "elementType": "all", "stylers": [{"color": "#f9f5ed"}]},{"featureType": "road.highway", "elementType": "all", "stylers": [{"color": "#f5f5f5"}]}, {"featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{"color": "#c9c9c9"}]}, {"featureType": "water", "elementType": "all", "stylers": [{"color": "#aee0f4"}]}],
			disableDefaultUI: true,
		});
	}

	#fetchPlaces() {
		CmsApi.fetchPlaces().then(places => {
			places.forEach((place) => {
				this.#setMarkers(place);
			});
		});
	}

	#initMarkers() {
		this.#toggleButtons.forEach(btn => {
			this.#markers[btn.dataset.placeCategory] = [];
		});
	}

	/**
	 * @param {any} place
	 */
	#setMarkers(place) {
		const position = new google.maps.LatLng(place.latitude, place.longitude);
		let marker = new google.maps.Marker({
			id: place.id,
			position: position,
			map: this.#googleMap,
			icon: window.config.themeDirectory + '/img/marker.svg'
		});

		place.categories.forEach(category => {
			this.#markers[category.name].push(marker);
		});

		// place.categories.forEach(c => {
		// 	this.#markerGroups[c.id].push(marker);
		// 	this.#markerGroups[0].includes(marker) ? this.#markerGroups[0].push() : this.#markerGroups[0].push(marker);
		// });
		this.#bounds.extend(position);
		this.#googleMap.fitBounds(this.#bounds, {top: 250, right: 63, bottom: 63, left: 63});
		this.#googleMap.setZoom(16);
		this.#setInfoBoxes(place, marker);
	}

	/**
	 * @param {any} place
	 * @param {google.maps.Marker} marker
	 */
	#setInfoBoxes(place, marker) {
		let address = place.address? '<p>' + place.address + '</p>' : '',
			phone = place.phone? '<a href="tel:' + place.phone + '">' + place.phone + '</a>' : '',
			email = place.email? '<a href="mailto:' + place.email + '">' + place.email + '</a>' : '',
			website = place.website? '<a href="' + place.website + '" target="_blank" class="website-url">' + place.website + '</a>' : '',
			infoBox = new SnazzyInfoWindow({
				marker: marker,
				content: '<h4>' + place.name + '</h4>' + address + phone + email + website,
				closeOnMapClick: true,
				closeWhenOthersOpen: true
			});

		marker.addListener('click', function() {
			infoBox.open();
		});
	}

	#setEventListenersOnToggleButtons() {
		let allButton = this.#holderElement.querySelector('#show-all-todo');

		for (let i = 0; i < this.#toggleButtons.length; i++) {
			this.#toggleButtons[i].addEventListener('click', function (event) {

				let isAll = event.target.dataset.placeCategory === 'all',
					hasAllBtnActiveClass = allButton.classList.contains('active');

				if (isAll) {
					for (let j = 0; j < this.#toggleButtons.length; j++) {
						this.#toggleButtons[j].classList.remove('active');
					}

					if (hasAllBtnActiveClass) {
						allButton.classList.remove('active');
						this.#toggleMarkersOfType(event.target.dataset.placeCategory, true);
					} else {
						allButton.classList.add('active');
						this.#toggleMarkersOfType(event.target.dataset.placeCategory, false);
					}
				} else {
					allButton.classList.remove('active');
					event.target.classList.toggle('active');
					this.#toggleMarkersOfType(event.target.dataset.placeCategory, hasAllBtnActiveClass);
				}

			}.bind(this), false);
		}
	}

	/**
	 * @param {String} category
	 * @param {Boolean} clearAll
	 */
	#toggleMarkersOfType(category, clearAll) {
		this.#clearMarkers = !clearAll;
		if (category==='all') {
			this.#clearAllMarkers();
		} else {
			if (clearAll) {
				this.#clearAllMarkers();
			}
			for (let i = 0; i < this.#markers[category].length; i++) {
				this.#markers[category][i].setVisible(!this.#markers[category][i].getVisible());
			}
		}
	}

	#clearAllMarkers() {
		for (let i = 0; i < this.#toggleButtons.length; i++) {
			let key = this.#toggleButtons[i].dataset.placeCategory;
			for (let j = 0; j < this.#markers[key].length; j++) {
				this.#markers[key][j].setVisible(this.#clearMarkers);
			}
		}
	}

	#setThingsToDoMap() {
		let thingsToDoMap = document.querySelector('.things-todo-map'),
			self = this;
		if(thingsToDoMap){
			jQuery(function ($) {
				$.fn.isInViewport = function() {
					let elementTop = $(this).offset().top,
						viewportTop = $(window).scrollTop(),
						viewport = viewportTop + window.innerHeight;

					return viewport > elementTop;
				};

				if($('.things-todo-map').isInViewport()){
					self.#initMarkers();
				}
				else{
					$('.things-todo-map').on('lazybeforeunveil', function(e){
						self.#initMarkers();
					});
				}
			});
		}
	}
}
